import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';

import { OrderDeclineType } from 'types/enums/OrderDeclineType';
import { OrderStatus } from 'types/enums/OrderStatus';
import { Package } from 'types/interfaces/Package';

import { PaymentApi } from 'api/PaymentApi';
import { assertString } from 'helpers/utils/checkType';
import { useToast } from 'hooks/useToast';
import {
  oneClickPaymentReset,
  setOneClickPaymentCardToken,
  setOneClickPaymentDeclineType,
  setOneClickPaymentOrderId,
  setOneClickPaymentStatus,
} from 'store/oneClickPayment/oneClickPaymentSlice';
import {
  getOneClickPaymentCardToken,
  getOneClickPaymentDeclineType,
  getOneClickPaymentStatus,
  getOneClickPaymentVerifyUrl,
} from 'store/oneClickPayment/selectors';
import {
  microOneClickPay,
  microOneClickPayResign,
} from 'store/oneClickPayment/thunks';

type Input = {
  pack: Package | undefined;
  payFn?: (payload: {
    packId: string;
    cardToken: string;
  }) => Promise<AxiosResponse<any>>;
  payResignFn?: (payload: {
    packId: string;
    cardToken: string;
    cvv: string;
  }) => Promise<AxiosResponse<any>>;
  onSuccess?: () => void;
  onFail?: () => void;
};

type Output = {
  verifyUrl: string | null;
  handlePayment: (
    payload: { cvv: string | undefined },
    cardFailCb?: () => void
  ) => void;
};

export const useMicroPayment = (params: Input): Output => {
  const dispatch = useDispatch();

  const cardToken = useSelector(getOneClickPaymentCardToken);
  const status = useSelector(getOneClickPaymentStatus);
  const declineType = useSelector(getOneClickPaymentDeclineType);
  const verifyUrl = useSelector(getOneClickPaymentVerifyUrl);

  const { showErrorToast } = useToast();

  useEffect(() => {
    const localFetchCardToken = async () => {
      try {
        const { data } = await PaymentApi.fetchCreditCard();

        dispatch(setOneClickPaymentCardToken(data.card.id));
      } catch (err) {
        showErrorToast({
          title: 'Something went wrong',
          subtitle: 'Please, try again later',
        });
      }
    };

    localFetchCardToken();
  }, [dispatch, showErrorToast]);

  useEffect(() => {
    if (status === 'success') {
      params.onSuccess?.();
      dispatch(oneClickPaymentReset());
    }
  }, [dispatch, params, status]);

  useEffect(() => {
    if (
      declineType === OrderDeclineType.Final ||
      declineType === OrderDeclineType.MethodFinal ||
      declineType === OrderDeclineType.InsufficientFunds
    ) {
      showErrorToast({
        title: 'Your Payment Method Was Declined. Try it Later',
      });

      dispatch(oneClickPaymentReset());

      params.onFail && params.onFail();
    }
  }, [declineType, dispatch, params, showErrorToast]);

  const handlePayment = (
    payload: Parameters<Output['handlePayment']>[0],
    cardFailCb: Parameters<Output['handlePayment']>[1]
  ) => {
    const { cvv } = payload;
    const packId = params.pack?.id;

    if (!assertString(packId) || !assertString(cardToken)) {
      showErrorToast({
        title: 'Something went wrong',
        subtitle: 'Please, try again later',
      });

      cardFailCb && cardFailCb();

      return;
    }

    dispatch(setOneClickPaymentOrderId(null));
    dispatch(setOneClickPaymentStatus(OrderStatus.Process));
    dispatch(setOneClickPaymentDeclineType(null));

    if (assertString(cvv)) {
      dispatch(
        microOneClickPayResign({
          cardToken,
          cvv,
          packId,
          payFn: params.payResignFn ?? PaymentApi.microOneClickPayResign,
        })
      );

      return;
    }

    dispatch(
      microOneClickPay({
        packId,
        cardToken,
        payFn: params.payFn ?? PaymentApi.microOneClickPay,
      })
    );
  };

  return {
    verifyUrl,
    handlePayment,
  };
};
